import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationViewLogRepository, LocationViewStats, MeetingGroup, ViewerEntry } from 'repository/locationViewLogRepository';
import { AppScreen, Badge, InfoGrid, Section } from 'components/ds';
import { IcCalendar, IcClose } from 'components/common/icons/EmojiIcons';
import styles from './MyLocationViews.module.css';

const formatTimeOnly = (iso: string | null | undefined) => {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mi}`;
    } catch {
        return iso;
    }
};

export const MyLocationViews: React.FC = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState<LocationViewStats | null>(null);
    const [yearMonth, setYearMonth] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [showMonthPicker, setShowMonthPicker] = useState(false);

    const formatDateLabel = (yyyymmdd: string) => {
        if (!yyyymmdd) return '';
        try {
            const [y, m, d] = yyyymmdd.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            const weekDaysRaw = t('myLocationViews.weekDays', { returnObjects: true }) as unknown;
            const weekDays = Array.isArray(weekDaysRaw) ? weekDaysRaw as string[] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const week = weekDays[date.getDay()] || '';
            return t('myLocationViews.monthDayWeekLabel', { month: m, day: d, week });
        } catch {
            return yyyymmdd;
        }
    };

    const formatYearMonthLabel = (ym: string) => {
        if (!ym) return '';
        const [y, m] = ym.split('-');
        return t('myLocationViews.yearMonthLabel', { year: y, month: parseInt(m, 10) });
    };

    const load = (ym?: string) => {
        setLoading(true);
        LocationViewLogRepository.onGetMyStats(ym)
            .then((res: any) => {
                const data: LocationViewStats = res?.data?.data || res?.data;
                setStats(data || null);
                if (data?.yearMonth) setYearMonth(data.yearMonth);
            })
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSelectMonth = (ym: string) => {
        setShowMonthPicker(false);
        if (ym !== yearMonth) {
            setYearMonth(ym);
            load(ym);
        }
    };

    const groups = stats?.byGroup || [];
    const empty = !loading && groups.length === 0;

    // 모임 단위로 묶고, 그 안에서 일자 카드 리스트 — TRAVEL 의 일자별 분리를 시각화하려면
    // (meetingId, viewDate) 단위 그룹을 그대로 쓰되, 같은 meetingId 가 연속이면 헤더만 1번 표시.
    const renderedGroups = useMemo(() => {
        const out: { showMeetingHeader: boolean; group: MeetingGroup }[] = [];
        let prevMeetingId = '';
        for (const g of groups) {
            out.push({ showMeetingHeader: g.meetingId !== prevMeetingId, group: g });
            prevMeetingId = g.meetingId;
        }
        return out;
    }, [groups]);

    return (
        <AppScreen title={t('myLocationViews.title')} bar="sub">
            {/* 월 고르기 */}
            <Section spacing="tight">
                <button
                    type="button"
                    className={styles.month}
                    onClick={() => setShowMonthPicker(true)}
                    disabled={!stats?.availableMonths?.length}
                >
                    <span className={styles.monthIcon}><IcCalendar size={16} /></span>
                    <span className={styles.monthLabel}>
                        {yearMonth ? formatYearMonthLabel(yearMonth) : t('myLocationViews.noData')}
                    </span>
                </button>

                {!loading && stats && yearMonth && (
                    <div className={styles.summary}>
                        <InfoGrid
                            items={[
                                { label: t('myLocationViews.totalViews'), value: String(stats.totalViews) },
                                { label: t('myLocationViews.uniqueViewers'), value: String(stats.uniqueViewers) },
                            ]}
                        />
                    </div>
                )}
            </Section>

            {loading && <p className={styles.state}>{t('myLocationViews.loading')}</p>}

            {empty && (
                <p className={styles.state}>
                    <span className={styles.stateTitle}>{t('myLocationViews.emptyTitle')}</span>
                    {t('myLocationViews.emptyDesc')}
                </p>
            )}

            {/* 모임 + 날짜별 열람 기록 */}
            {!loading && renderedGroups.map(({ showMeetingHeader, group }, idx) => (
                <Section key={`${group.meetingId}-${group.viewDate}-${idx}`} spacing="tight">
                    {showMeetingHeader && (
                        <div className={styles.meetingHead}>
                            <Badge tone={group.meetingType === 'TRAVEL' ? 'lime' : 'neutral'}>
                                {group.meetingType === 'TRAVEL'
                                    ? t('myLocationViews.tagTravel')
                                    : t('myLocationViews.tagDay')}
                            </Badge>
                            <span className={styles.meetingName}>
                                {group.meetingName || t('myLocationViews.fallbackMeeting')}
                            </span>
                        </div>
                    )}
                    <div className={styles.dayLabel}>{formatDateLabel(group.viewDate)}</div>
                    <div className={styles.viewers}>
                        {group.viewers.map((v: ViewerEntry, vi) => (
                            <div key={`${v.userNo}-${v.viewType}-${vi}`} className={styles.viewer}>
                                <span className={styles.avatar}>
                                    {v.profileImgPath
                                        ? <img src={v.profileImgPath} alt="" />
                                        : (v.nickname || '?').charAt(0)}
                                </span>
                                <span className={styles.viewerBody}>
                                    <span className={styles.viewerTop}>
                                        <span className={styles.viewerName}>
                                            {v.nickname || t('myLocationViews.fallbackUser', { userNo: v.userNo })}
                                        </span>
                                        {v.viewType === 'SAFE_RETURN' && (
                                            <Badge tone="outline">{t('myLocationViews.safeReturnBadge')}</Badge>
                                        )}
                                    </span>
                                    <span className={styles.viewerMeta}>
                                        {t('myLocationViews.viewCount', { count: v.viewCount })}
                                        {' · '}
                                        {formatTimeOnly(v.firstViewedAt)} ~ {formatTimeOnly(v.lastViewedAt)}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                </Section>
            ))}

            {/* 월 고르기 시트 */}
            {showMonthPicker && (
                <div className={styles.overlay} onClick={() => setShowMonthPicker(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('myLocationViews.monthSelect')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('myLocationViews.monthSelect')}</h2>
                            <button
                                type="button"
                                className={styles.sheetClose}
                                onClick={() => setShowMonthPicker(false)}
                                aria-label={t('common.close')}
                            >
                                <IcClose size={20} />
                            </button>
                        </div>
                        <div className={styles.sheetBody}>
                            {(stats?.availableMonths || []).map(ym => (
                                <button
                                    key={ym}
                                    type="button"
                                    className={ym === yearMonth ? `${styles.monthItem} ${styles.monthItemOn}` : styles.monthItem}
                                    onClick={() => onSelectMonth(ym)}
                                >
                                    {formatYearMonthLabel(ym)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </AppScreen>
    );
};

export default MyLocationViews;
